const JWT_STORAGE_KEY = 'self-hosted-jwt';
const JWT_CHANGE_EVENT = 'self-hosted-jwt-changed';

function emitJwtChange() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(JWT_CHANGE_EVENT));
}

function base64UrlToBase64(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = normalized.length % 4;

  if (paddingLength === 0) {
    return normalized;
  }

  return `${normalized}${'='.repeat(4 - paddingLength)}`;
}

export function decodeJwtPayload<T = Record<string, unknown>>(token: string | null): T | null {
  if (!token) {
    return null;
  }

  const payloadSegment = token.split('.')[1];

  if (!payloadSegment) {
    return null;
  }

  try {
    return JSON.parse(atob(base64UrlToBase64(payloadSegment))) as T;
  } catch {
    return null;
  }
}

export function getJwtToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return localStorage.getItem(JWT_STORAGE_KEY);
}

export function setJwtToken(token: string): void {
  localStorage.setItem(JWT_STORAGE_KEY, token);
  emitJwtChange();
}

export function clearJwtToken(): void {
  localStorage.removeItem(JWT_STORAGE_KEY);
  emitJwtChange();
}

export function subscribeToJwtTokenChange(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === JWT_STORAGE_KEY) {
      listener();
    }
  };
  const onTokenChange = () => listener();

  window.addEventListener('storage', onStorage);
  window.addEventListener(JWT_CHANGE_EVENT, onTokenChange);

  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(JWT_CHANGE_EVENT, onTokenChange);
  };
}

export function isJwtValid(token: string | null): boolean {
  const payload = decodeJwtPayload<{ exp?: number }>(token);

  if (!payload || typeof payload.exp !== 'number') {
    return false;
  }

  return Date.now() < payload.exp * 1000;
}
